import { action, computed, makeObservable, observable } from "mobx";
import PodCatalogAPI from "../api/PodCatalogAPI";
import MockupConfigAPI from "../api/MockupConfigAPI";
import type { PlacementType, PSDTemplate } from "../../../models/MockupConfig/index";
import {
  PodCatalogListQuery,
  PodCatalogProduct,
  PodCatalogProductDetail,
  PodCatalogVariant,
  PodCatalogVariantUpsert,
  PodCatalogVariantDrawerRow,
  PodCatalogUpsertBody,
  extractPodCatalogImageUrls,
  PodCatalogPaginated,
  defaultPodProductSku,
  sanitizePodCatalogVariantsBody,
} from "../../../models/Products/PodCatalog";

export class PodCatalogStore {
  podCatalogAPI: PodCatalogAPI;
  mockupConfigAPI: MockupConfigAPI;
  list: PodCatalogProduct[];
  limit: number;
  total_pages: number;
  total_count: number;
  page_no: number;
  error: string | null;
  detail: PodCatalogProductDetail | null;
  detailLoading: boolean;
  detailError: string | null;
  detailNotFound: boolean;

  detailPodPrice: string;
  detailProductSku: string;
  detailInitialPodPrice: string;
  detailInitialProductSku: string;
  detailSaving: boolean;
  detailAddingToPod: boolean;
  detailRemovingFromPod: boolean;
  isRemovedFromPod: boolean;
  detailRemovingVariantId: number | null;
  detailVariantDrawerRows: PodCatalogVariantDrawerRow[];
  detailVariantSavingErrors: Record<string, string>;
  detailVariantDeletingErrors: Record<string, string>;
  detailPsdFile: File | null;
  detailPsdPlacementTypeId: string;
  detailPsdVariantSku: string;
  detailPsdPlacementTypes: PlacementType[];
  detailPsdTemplates: PSDTemplate[];

  constructor() {
    this.podCatalogAPI = new PodCatalogAPI();
    this.mockupConfigAPI = new MockupConfigAPI();
    this.list = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.page_no = 1;
    this.error = null;
    this.detail = null;
    this.detailLoading = false;
    this.detailError = null;
    this.detailNotFound = false;
    this.detailPodPrice = "";
    this.detailProductSku = "";
    this.detailInitialPodPrice = "";
    this.detailInitialProductSku = "";
    this.detailSaving = false;
    this.detailAddingToPod = false;
    this.detailRemovingFromPod = false;
    this.isRemovedFromPod = false;
    this.detailRemovingVariantId = null;
    this.detailVariantDrawerRows = [];
    this.detailVariantSavingErrors = {};
    this.detailVariantDeletingErrors = {};
    this.detailPsdFile = null;
    this.detailPsdPlacementTypeId = "";
    this.detailPsdVariantSku = "";
    this.detailPsdPlacementTypes = [];
    this.detailPsdTemplates = [];

    makeObservable(this, {
      list: observable,
      limit: observable,
      total_pages: observable,
      total_count: observable,
      page_no: observable,
      error: observable,
      detail: observable,
      detailLoading: observable,
      detailError: observable,
      detailNotFound: observable,
      detailPodPrice: observable,
      detailProductSku: observable,
      detailInitialPodPrice: observable,
      detailInitialProductSku: observable,
      detailSaving: observable,
      detailAddingToPod: observable,
      detailRemovingFromPod: observable,
      isRemovedFromPod: observable,
      detailRemovingVariantId: observable,
      detailVariantDrawerRows: observable,
      detailVariantSavingErrors: observable,
      detailVariantDeletingErrors: observable,
      detailPsdFile: observable,
      detailPsdPlacementTypeId: observable,
      detailPsdVariantSku: observable,
      detailPsdPlacementTypes: observable,
      detailPsdTemplates: observable,
      clearDetailPsdCard: action,
      setDetailPsdPlacementTypes: action,
      setProductPsdTemplates: action,
      setDetailPsdFile: action,
      setDetailPsdPlacementTypeId: action,
      setDetailPsdVariantSku: action,
      initDetailVariantDrawerFromRows: action,
      clearDetailVariantDrawer: action,
      setDetailVariantDrawerRows: action,
      addDetailVariantDrawerRow: action,
      updateDetailVariantDrawerRow: action,
      setDetailVariantRowEditing: action,
      cancelDetailVariantRowEdit: action,
      removeDetailVariantDrawerRow: action,
      clearDetailVariantSavingError: action,
      setDetailVariantSavingError: action,
      clearDetailVariantDeletingError: action,
      setDetailVariantDeletingError: action,
      syncDetailFormFromDetail: action,
      setDetailRemovingVariantId: action,
      setDetailPodPrice: action,
      setDetailProductSku: action,
      resetDetailFormToInitial: action,
      setDetailSaving: action,
      setDetailAddingToPod: action,
      setDetailRemovingFromPod: action,
      setIsRemovedFromPod: action,
      hasDetailFormChanges: computed,
      beginDetailLoad: action,
      setDetailFromFetch: action,
      setDetailFetchError: action,
      finishDetailLoad: action,
      setPodCatalogList: action,
      setPodCatalogListError: action,
      clearPodCatalogDetail: action,
      reset: action,
      applyPodCatalogDetailPatch: action,
      applyPodCatalogVariantsLocally: action,
      fetchPodCatalog: action,
      fetchPodCatalogDetail: action,
      addToPod: action,
      removeFromPod: action,
      upsertPodCatalogDetail: action,
      upsertPodCatalogVariants: action,
      savePodCatalogMedia: action,
      fetchProductPsdTemplates: action,
      loadProductPsdTemplates: action,
      uploadProductPsd: action,
      setDefaultProductPsdTemplate: action,
      removeProductPsdTemplate: action,
    });
  }

  reset = () => {
    this.clearPodCatalogDetail();
    this.list = [];
    this.limit = 30;
    this.total_pages = 0;
    this.total_count = 0;
    this.page_no = 1;
    this.error = null;
  };

  clearPodCatalogDetail = () => {
    this.detail = null;
    this.detailLoading = false;
    this.detailError = null;
    this.detailNotFound = false;
    this.detailPodPrice = "";
    this.detailProductSku = "";
    this.detailInitialPodPrice = "";
    this.detailInitialProductSku = "";
    this.detailSaving = false;
    this.detailAddingToPod = false;
    this.detailRemovingFromPod = false;
    this.isRemovedFromPod = false;
    this.detailRemovingVariantId = null;
    this.clearDetailVariantDrawer();
    this.clearDetailPsdCard();
  };

  clearDetailPsdCard = () => {
    this.detailPsdFile = null;
    this.detailPsdPlacementTypeId = "";
    this.detailPsdVariantSku = "";
    this.detailPsdPlacementTypes = [];
    this.detailPsdTemplates = [];
  };

  setDetailPsdPlacementTypes = (placementTypes: PlacementType[]) => {
    this.detailPsdPlacementTypes = [...placementTypes].sort(
      (a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99)
    );
  };

  setProductPsdTemplates = (templates: PSDTemplate[]) => {
    this.detailPsdTemplates = [...templates];
  };

  setDetailPsdFile = (file: File | null) => {
    this.detailPsdFile = file;
  };

  setDetailPsdVariantSku = (variantSku: string) => {
    this.detailPsdVariantSku = variantSku;
  };

  setDetailPsdPlacementTypeId = (placementTypeId: string) => {
    this.detailPsdPlacementTypeId = placementTypeId;
  };

  clearDetailVariantDrawer = () => {
    this.detailVariantDrawerRows = [];
    this.detailVariantSavingErrors = {};
    this.detailVariantDeletingErrors = {};
  };

  initDetailVariantDrawerFromRows = (initialRows: PodCatalogVariantUpsert[] = []) => {
    this.detailVariantDrawerRows = initialRows.map((row, idx) => {
      const resolvedVariantId = Number(row.variant_id ?? row.id ?? 0);
      return {
        ...row,
        variant_id: resolvedVariantId > 0 ? resolvedVariantId : (row.variant_id as number),
        rowId: `pod-v-${idx}-${Date.now()}`,
        isEditing: false,
        needsVariantPick: !(resolvedVariantId > 0),
      };
    });
    this.detailVariantSavingErrors = {};
    this.detailVariantDeletingErrors = {};
  };

  setDetailVariantDrawerRows = (rows: PodCatalogVariantDrawerRow[]) => {
    this.detailVariantDrawerRows = rows;
  };

  addDetailVariantDrawerRow = () => {
    const rowId = `${Date.now()}-${Math.random()}`;
    this.detailVariantDrawerRows = [
      ...this.detailVariantDrawerRows,
      {
        rowId,
        id: undefined,
        variant_id: 0,
        color: "",
        sku: "",
        variant_sku: undefined,
        isEditing: true,
        needsVariantPick: true,
      },
    ];
  };

  updateDetailVariantDrawerRow = (
    rowId: string,
    key: keyof PodCatalogVariantUpsert,
    value: string | number
  ) => {
    this.detailVariantDrawerRows = this.detailVariantDrawerRows.map((row) =>
      row.rowId === rowId ? { ...row, [key]: value } : row
    );
  };

  setDetailVariantRowEditing = (rowId: string, isEditing: boolean) => {
    this.detailVariantDrawerRows = this.detailVariantDrawerRows.map((row) =>
      row.rowId === rowId ? { ...row, isEditing } : row
    );
  };

  cancelDetailVariantRowEdit = (rowId: string) => {
    const target = this.detailVariantDrawerRows.find((row) => row.rowId === rowId);
    if (!target) return;
    // Unsaved new rows have no persisted mapping id yet.
    if (target.id == null || target.needsVariantPick) {
      this.removeDetailVariantDrawerRow(rowId);
      return;
    }
    this.setDetailVariantRowEditing(rowId, false);
  };

  removeDetailVariantDrawerRow = (rowId: string) => {
    this.detailVariantDrawerRows = this.detailVariantDrawerRows.filter(
      (row) => row.rowId !== rowId
    );
  };

  clearDetailVariantSavingError = (rowId: string) => {
    if (!this.detailVariantSavingErrors[rowId]) return;
    const next = { ...this.detailVariantSavingErrors };
    delete next[rowId];
    this.detailVariantSavingErrors = next;
  };

  setDetailVariantSavingError = (rowId: string, message: string) => {
    this.detailVariantSavingErrors = {
      ...this.detailVariantSavingErrors,
      [rowId]: message,
    };
  };

  clearDetailVariantDeletingError = (rowId: string) => {
    if (!this.detailVariantDeletingErrors[rowId]) return;
    const next = { ...this.detailVariantDeletingErrors };
    delete next[rowId];
    this.detailVariantDeletingErrors = next;
  };

  setDetailVariantDeletingError = (rowId: string, message: string) => {
    this.detailVariantDeletingErrors = {
      ...this.detailVariantDeletingErrors,
      [rowId]: message,
    };
  };

  syncDetailFormFromDetail = () => {
    const detail = this.detail;
    if (!detail) return;
    const initialPodPrice =
      detail.swageazy_selling_price != null
        ? String(Number(detail.swageazy_selling_price))
        : "";
    const pid = detail.product_id ?? detail.id;
    const productSku =
      detail.pod_catalogue_sku?.trim() ||
      (pid != null && Number(pid) > 0 ? defaultPodProductSku(Number(pid)) : "");
    this.detailPodPrice = initialPodPrice;
    this.detailProductSku = productSku;
    this.detailInitialPodPrice = initialPodPrice;
    this.detailInitialProductSku = productSku;
    this.isRemovedFromPod = false;
  };

  setDetailRemovingVariantId = (variantId: number | null) => {
    this.detailRemovingVariantId = variantId;
  };

  setDetailPodPrice = (v: string) => {
    this.detailPodPrice = v;
  };

  setDetailProductSku = (v: string) => {
    this.detailProductSku = v;
  };

  resetDetailFormToInitial = () => {
    this.detailPodPrice = this.detailInitialPodPrice;
    this.detailProductSku = this.detailInitialProductSku;
  };

  setDetailSaving = (v: boolean) => {
    this.detailSaving = v;
  };

  setDetailAddingToPod = (v: boolean) => {
    this.detailAddingToPod = v;
  };

  setDetailRemovingFromPod = (v: boolean) => {
    this.detailRemovingFromPod = v;
  };

  setIsRemovedFromPod = (v: boolean) => {
    this.isRemovedFromPod = v;
  };

  get hasDetailFormChanges(): boolean {
    return (
      this.detailPodPrice.trim() !== this.detailInitialPodPrice.trim() ||
      this.detailProductSku.trim() !== this.detailInitialProductSku.trim()
    );
  }

  beginDetailLoad = () => {
    this.detailLoading = true;
    this.detailError = null;
    this.detailNotFound = false;
    this.detail = null;
  };

  setDetailFromFetch = (data: PodCatalogProductDetail) => {
    this.detail = data;
    this.syncDetailFormFromDetail();
  };

  setDetailFetchError = (message: string, notFound = false) => {
    if (notFound) {
      this.detailNotFound = true;
    } else {
      this.detailError = message;
    }
  };

  finishDetailLoad = () => {
    this.detailLoading = false;
  };

  setPodCatalogList = (response: PodCatalogPaginated<PodCatalogProduct>) => {
    this.limit = response.limit;
    this.total_pages = response.total_pages;
    this.total_count = response.total_count;
    this.page_no = response.page_no;
    this.list = response.list || [];
    this.error = null;
  };

  setPodCatalogListError = (message: string) => {
    this.error = message;
  };

  applyPodCatalogDetailPatch = (
    body: PodCatalogUpsertBody,
    result?: Partial<PodCatalogProductDetail>
  ) => {
    if (!this.detail) return;
    this.detail = {
      ...this.detail,
      ...result,
      ...(body.product_sku !== undefined && {
        pod_catalogue_sku: body.product_sku,
      }),
      ...(body.swageazy_selling_price !== undefined && {
        swageazy_selling_price: body.swageazy_selling_price,
      }),
      ...(body.country_of_origin !== undefined && {
        country_of_origin: body.country_of_origin,
      }),
      ...(body.production_time !== undefined && {
        production_time: body.production_time,
      }),
    };
    this.syncDetailFormFromDetail();
  };

  applyPodCatalogVariantsLocally = (
    variants: PodCatalogVariantUpsert[],
    resultVariants?: PodCatalogVariant[]
  ) => {
    if (!this.detail) return;
    if (resultVariants && Array.isArray(resultVariants)) {
      this.detail = { ...this.detail, pod_catalog_variants: resultVariants };
      return;
    }

    const productId = this.detail.product_id ?? 0;
    const existingById = new Map(
      (this.detail.pod_catalog_variants || []).map((variant) => [
        variant.id,
        variant,
      ])
    );
    const existingByVariantId = new Map(
      (this.detail.pod_catalog_variants || [])
        .filter((variant) => variant.variant_id != null)
        .map((variant) => [variant.variant_id as number, variant])
    );

    const nextVariants: PodCatalogVariant[] = variants.map((upsert, index) => {
      const existing =
        (upsert.id != null ? existingById.get(upsert.id) : undefined) ||
        (upsert.variant_id != null
          ? existingByVariantId.get(upsert.variant_id)
          : undefined);

      return {
        id: upsert.id ?? existing?.id ?? index + 1,
        variant_id: upsert.variant_id ?? existing?.variant_id ?? null,
        product_id: existing?.product_id ?? productId,
        color: upsert.color ?? existing?.color ?? null,
        sku: upsert.sku ?? existing?.sku ?? null,
        variant_sku: upsert.variant_sku ?? existing?.variant_sku ?? null,
        active: existing?.active ?? 1,
      };
    });

    this.detail = { ...this.detail, pod_catalog_variants: nextVariants };
  };

  fetchPodCatalog = async (params: PodCatalogListQuery) => {
    const response = await this.podCatalogAPI.listPodCatalog(params);
    this.setPodCatalogList(response);
  };

  fetchPodCatalogDetail = async (productId: number) => {
    this.beginDetailLoad();
    try {
      const data = await this.podCatalogAPI.getPodCatalogProduct(productId);
      this.setDetailFromFetch(data);
    } catch (e: unknown) {
      const err = e as {
        response?: { status?: number; data?: { message?: string } };
        message?: string;
      };
      if (err?.response?.status === 404) {
        this.setDetailFetchError("", true);
      } else {
        this.setDetailFetchError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load POD product."
        );
      }
      throw e;
    } finally {
      this.finishDetailLoad();
    }
  };

  addToPod = (productId: number) =>
    this.podCatalogAPI.addPodCatalogProduct(productId);

  removeFromPod = (productId: number) =>
    this.podCatalogAPI.removePodCatalogProduct(productId);

  upsertPodCatalogDetail = async (
    productId: number,
    body: PodCatalogUpsertBody
  ) => {
    const res = await this.podCatalogAPI.upsertPodCatalogProduct(productId, body);
    const result = res?.result as Partial<PodCatalogProductDetail> | undefined;
    this.applyPodCatalogDetailPatch(body, result);
    return res;
  };

  upsertPodCatalogVariants = async (
    productId: number,
    variants: PodCatalogVariantUpsert[],
    options?: { allowEdit?: boolean }
  ) => {
    if (options?.allowEdit === false) {
      throw new Error("You do not have permission to edit POD catalog variants.");
    }
    const sanitizedBody = sanitizePodCatalogVariantsBody({ variants });
    const res = await this.podCatalogAPI.upsertPodCatalogVariants(
      productId,
      sanitizedBody
    );
    const result = res?.result as
      | { variants?: PodCatalogVariant[]; pod_catalog_variants?: PodCatalogVariant[] }
      | undefined;
    const resultVariants = result?.variants ?? result?.pod_catalog_variants;
    this.applyPodCatalogVariantsLocally(sanitizedBody.variants, resultVariants);
    return res;
  };

  savePodCatalogMedia = async (productId: number, imageUrls: string[]) => {
    const payload = { images: extractPodCatalogImageUrls(imageUrls) };
    const res = await this.podCatalogAPI.upsertPodCatalogImages(productId, payload);
    if (this.detail) {
      this.detail = {
        ...this.detail,
        pod_catalog_images: payload.images,
      };
    }
    return res;
  };

  fetchProductPsdTemplates = async (productId: number) =>
    this.mockupConfigAPI.getPSDTemplates(productId);

  loadProductPsdTemplates = async (productId: number) => {
    const templates = await this.fetchProductPsdTemplates(productId);
    this.setProductPsdTemplates(templates);
    return templates;
  };

  uploadProductPsd = (payload: {
    file: File | null;
    product_id: number;
    reference_id?: string;
    placetype_id: number;
    variant_sku?: string | null;
  }) => {
    if (!payload.file) {
      return Promise.reject(new Error("Select a PSD file before uploading."));
    }
    return this.mockupConfigAPI.uploadPsd({ ...payload, file: payload.file });
  };

  getProductPsdTemplateDownloadUrl = (
    productId: number,
    templateId: number,
  ): string => {
    return this.mockupConfigAPI.downloadProductPsdTemplate(productId, templateId);
  };

  setDefaultProductPsdTemplate = async (
    productId: number,
    templateId: number,
  ) => {
    const res = await this.mockupConfigAPI.setDefaultProductPsdTemplate(
      productId,
      templateId,
    );
    this.detailPsdTemplates = this.detailPsdTemplates.map((t) => ({
      ...t,
      is_default: t.id === templateId,
    }));
    return res;
  };

  removeProductPsdTemplate = async (
    productId: number,
    templateId: number,
  ) => {
    const res = await this.mockupConfigAPI.removeProductPsdTemplate(
      productId,
      templateId,
    );
    this.detailPsdTemplates = this.detailPsdTemplates.filter(
      (t) => t.id !== templateId,
    );
    return res;
  };
}
